import { usePathname } from "@-/com.npmjs.expo-router";
import useLatest from "@-/com.npmjs.use-latest";
import { useOnboardingState } from "@-/dev.improve.haniverse.lib.onboarding";
import {
  isOnboardingActive,
  isOnboardingDestinationUnlocked,
} from "@-/dev.improve.haniverse.lib.onboarding.state";
import { useIsActorSyncPreparing } from "@-/dev.improve.haniverse.routes.nav._context";
import { useMapPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.map.navigator";
import { useEffect } from "react";
import { getOnboardingPlace } from "#onboarding-place";

/**
 * How long Learn's stack is left to settle before the map is stacked over it: a new learner's
 * summary redirects straight into its first slide, and the map belongs above that.
 */
const SETTLE_MS = 300;

/**
 * Keeps Learn on its map until the onboarding unlocks Learn (its globe, then the map's and
 * Browse's introductions), so the globe has the map's mainland to land on, and a relaunch
 * part-way through opens on the map. Once Learn is unlocked, Learn is still on the map, behind the
 * tab that has just become Learn's: opening it goes back to the cards (the summary, which hands
 * over to the first lesson).
 *
 * Startup and the welcome gate enter the map directly. Unlocking Learn must not send a relaunch
 * to the cards automatically: the learner opens them by pressing Learn or Continue Learning.
 */
export function useOnboardingLearnMap(): void {
  const state = useOnboardingState();
  const pathname = usePathname();
  const place = getOnboardingPlace(pathname);
  const isActorSyncPreparing = useIsActorSyncPreparing();
  const mapPageNavigator = useLatest(useMapPageNavigator());
  const isActive = isOnboardingActive(state);
  const isLearnUnlocked = isActive && isOnboardingDestinationUnlocked(state, "learn");
  const shouldOpenMap = isActive && !isLearnUnlocked && !isActorSyncPreparing && place === "cards";
  useEffect(() => {
    if (!shouldOpenMap) {
      return;
    }
    const timeout = setTimeout(() => mapPageNavigator.current.push.mutate(), SETTLE_MS);
    return () => clearTimeout(timeout);
    // Every move within Learn (the summary redirecting to its first slide) waits out the settle.
  }, [mapPageNavigator, pathname, shouldOpenMap]);
}
